import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — wraps content in a tag with a class derived
 * from a value→class lookup, useful for conditional Tailwind classes
 * driven by data (e.g. `imagePosition: "left"` → `@md:order-2`).
 *
 * The mapped class is appended to attributes.class (if any), space-separated.
 *
 * Two modes:
 *   - lookup (default): `map[value]` → class, falling back to `defaultClass`.
 *   - match: when `match` is set, compares it to `value` and applies
 *     `activeClass` when equal, `inactiveClass` otherwise. This is the
 *     declarative-equality escape hatch — it lets a `Switch` template compute
 *     a per-item active state (e.g. a category tab whose `~.key` equals the
 *     selected filter), which a static `map` cannot express because its keys
 *     are literal. Comparison is string-coerced so an ES bucket key
 *     (string/number) matches a stored scalar.
 *
 * YAML usage (lookup):
 *   - type: ClassMap
 *     value: ~~.imagePosition
 *     map:
 *       left: "@md:order-2"
 *       right: "@md:order-1"
 *     defaultClass: ""        # optional, used when value has no match
 *     tag: div                # optional, default "div"
 *     attributes:
 *       class: "min-w-0"
 *     content: [...]
 *
 * YAML usage (match — per-item active state inside a Switch):
 *   - type: ClassMap
 *     tag: span
 *     value: ~.key                              # current item's value
 *     match: ~~.selectedFilters.by_category     # the selected value
 *     activeClass: 'border-b-2 border-[#C9A961]'
 *     inactiveClass: 'border-b-2 border-transparent'
 *     content: ~.key
 */
export const ClassMap = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const value = evaluateTemplateValue({
    valueToEvaluate: props.value,
    globalDataContext,
    templateContext,
  })

  let mappedClass
  if (props.match !== undefined) {
    const match = evaluateTemplateValue({
      valueToEvaluate: props.match,
      globalDataContext,
      templateContext,
    })
    const isActive = String(value ?? '') === String(match ?? '')
    mappedClass = isActive ? (props.activeClass ?? '') : (props.inactiveClass ?? '')
  } else {
    const map = props.map ?? {}
    const defaultClass = props.defaultClass ?? ''
    mappedClass = Object.prototype.hasOwnProperty.call(map, value) ? map[value] : defaultClass
  }

  const evaluatedAttrs = useEvaluatedAttributes(props.attributes)
  const { className: baseClass, ...restAttrs } = evaluatedAttrs
  const composedClass = [baseClass, mappedClass].filter(Boolean).join(' ')

  const Tag = props.tag ?? 'div'

  return (
    <ActionDependant {...props}>
      <Tag {...restAttrs} className={composedClass || undefined}>
        {props.content && (
          <View
            currentData={currentData?.content}
            datafield="content"
            path={path + '.content'}
            props={props.content}
          />
        )}
      </Tag>
    </ActionDependant>
  )
}
